<template>
  <div class="panel-rows-wide">
    <!-- Product Grid Component -->
    <product-grid
      ref="productGrid"
      :selected-table="selectedTable"
      :edit-mode="editMode"
      :cash-reg-id="cashReg.id"
      :keyboard-shortcuts-enabled="keyboardShortcutsEnabled"
      :show-shortcut-badges="showShortcutBadges"
      @add-product="addProduct"
      @add-category="onCellClick"
      @tile-updated="onTileUpdated"
      @open-edit-dialog="editPanelItem"
      @open-add-dialog="addPanelItem">
    </product-grid>

    <v-row class="panels-row">
      <v-col cols="8" class="invoice-col">
        <!-- Invoice Panel Component -->
        <invoice-panel
          :invoice="invoice"
          :selected-table="selectedTable"
          :orders-view="ordersView"
          :cart-configuration="cartConfiguration"
          :currency="currency"
          :keyboard-shortcuts-enabled="keyboardShortcutsEnabled"
          :item-pending-quantities="itemPendingQuantities"
          @row-btn-click="onRowBtnClick"
          @change-quantity="onChangeQuantity"
          @open-edit-name-dialog="onNameClick"
          @open-discount-dialog="openDiscountSelectionDialog"
          @open-item-discount-dialog="openItemsDiscountSelectionDialog"
          @general-menu-click="generalMenuClick"
          @item-menu-click="itemMenuClick"
          @orders-change="onOrdersChange"
          @cart-discount-click="onCartDiscountClick"
          @open-table-note-dialog="openTableNoteDialog"
          @view-all-table-notes="viewAllTableNotes">
        </invoice-panel>
      </v-col>

      <v-col cols="4">
        <!-- Payment Panel Component -->
        <payment-panel
          :invoice="invoice"
          :selected-table="selectedTable"
          :cash-reg="cashReg"
          :selected-printer="selectedPrinter"
          :keyboard-shortcuts-enabled="keyboardShortcutsEnabled"
          :show-shortcut-badges="showShortcutBadges"
          :quantity-bar-enabled="quantityBarEnabled"
          :selected-quantity="selectedQuantity"
          @select-table="onSelectTable"
          @recalculate="recalculate"
          @clear-selection="clearSelection"
          @open-close-balance-dialog="onOpenCloseBalanceDialog"
          @open-search-items-dialog="onOpenSearchItemsDialog"
          @open-receipts-dialog="onOpenReceiptsDialog"
          @send-notes="onSendNotes"
          @reset-notes="onResetNodes"
          @create-new-table="onCreateNewTable"
          @add-write-in-product="onAddWriteInProduct"
          @quantity-changed="onQuantityChanged">
        </payment-panel>
      </v-col>
    </v-row>

    <!-- Total Row -->
    <div class="total-row">
      <v-row>
        <v-col align="start" cols="5">
          <h2 style="margin-left: 1rem; text-transform: uppercase">
            {{ `${selectedTable.name}` }}
          </h2>
        </v-col>
        <v-col class="pt-0" cols="7">
          <h4 style="text-transform: uppercase">
            Popust:&nbsp;
            {{ $options.filters.money(totalDiscount, 100, currency) }}
          </h4>
          <h3 style="text-transform: uppercase">
            {{ $t("$vuetify.receipts.receiptPrice") }}:
            {{ $options.filters.money(total, 100, currency) }}
          </h3>
        </v-col>
      </v-row>
    </div>

    <!-- Dialogs -->
    <!-- Add mode dialog -->
    <tile-panel-dialog
      v-if="!editItemDialogOpen"
      :isOpen="addItemDialogOpen"
      mode="add"
      :categories="categories"
      :selectedTile="selectedTile"
      @addCategoryToPanel="onAddCategoryToPanel"
      @addProductToPanel="onAddProductToPanel"
      @addItemDialogClose="onAddItemDialogClose">
    </tile-panel-dialog>

    <!-- Edit mode dialog -->
    <tile-panel-dialog
      v-if="editItemDialogOpen"
      :isOpen="editItemDialogOpen"
      mode="edit"
      :categories="categories"
      :editItem="editItem"
      @deleteItem="onDeleteItem"
      @changeCategoryOnPanel="onChangeCategoryOnPanel"
      @changeProductOnPanel="onChangeProductOnPanel"
      @editItemDialogClose="onEditItemDialogClose">
    </tile-panel-dialog>

    <select-product-from-category
      :selectProductDialogOpen="selectProductDialogOpen"
      :selectedCategoryId="selectedCategoryId"
      :selectedCategoryName="selectedCategoryName"
      :keyboardShortcutsEnabled="keyboardShortcutsEnabled"
      :showShortcutBadges="showShortcutBadges"
      :closeModalOnProductClick="closeModalOnProductClick"
      :displayMode="categoryDisplayMode"
      :itemQuantities="itemQuantities"
      :quantityBarEnabled="quantityBarEnabled"
      @addProduct="addProduct"
      @modifyProduct="handleModifyProduct"
      @setProductQuantity="setProductQuantity"
      @addItemDialogClose="onAddProductDialogClose">
    </select-product-from-category>

    <search-items-dialog
      v-if="searchItemsDialogOpen"
      :closeModalOnProductClick="closeModalOnProductClick"
      :displayMode="categoryDisplayMode"
      :itemQuantities="itemQuantities"
      :keyboardShortcutsEnabled="keyboardShortcutsEnabled"
      :quantityBarEnabled="quantityBarEnabled"
      @addProduct="addProduct"
      @modifyProduct="handleModifyProduct"
      @setProductQuantity="setProductQuantity"
      @closeSearchItemsDialog="onCloseSearchItemsDialog">
    </search-items-dialog>

    <receipts-dialog
      v-if="receiptsDialogOpen"
      @closeReceiptsDialog="onCloseReceiptsDialog">
    </receipts-dialog>

    <add-note-dialog
      ref="addNoteDialog"
      @note-saved="onTableNoteSaved">
    </add-note-dialog>

    <view-table-notes-dialog ref="viewTableNotesDialog"></view-table-notes-dialog>

    <add-note-article-dialog
      ref="addNoteArticleDialog"
      @open-discount-dialog="onOpenDiscountFromNote">
    </add-note-article-dialog>

    <menu-dialog
      ref="menuDialog"
      @add="addMenuProduct">
    </menu-dialog>

    <close-balance ref="closeBalance"></close-balance>

    <add-discount-form
      @add="onAddDiscount"
      ref="addDiscountForm">
    </add-discount-form>

    <v-container
      class="kbd"
      v-if="!$vuetify.breakpoint.smAndDown"
      ref="keyboard"
      v-show="oskVisible"
      :style="{ 'max-width': width }">
      <div class="kbd-header"></div>
      <vue-touch-keyboard
        :options="oskOptions"
        :layout="layout"
        :cancel="hideKbd"
        :accept="accept"
        :input="input" />
    </v-container>

    <show-error ref="showError"></show-error>

    <add-manual-price
      ref="addManualPrice"
      @add="onAddPrice">
    </add-manual-price>

    <discount-selection-dialog
      ref="discountSelectionDialog"
      :generalMenu="generalMenu"
      :lastInsertBy="lastInsertBy"
      @generalMenuClick="generalMenuClick">
    </discount-selection-dialog>

    <item-discount-selection-dialog-for-small-screen
      ref="itemDiscountSelectionDialog"
      @recalculate="recalculate"
      @itemMenuClick="itemMenuClick">
    </item-discount-selection-dialog-for-small-screen>
  </div>
</template>

<script>
import onScreenKeyboard from '@/mixins/onScreenKeyboard'
import Table from '@/model/Table'
import EventBus from '@/plugins/event-bus'
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'

// Always visible — keep static
import ProductGrid from './register-wide/ProductGrid.vue'
import InvoicePanel from './register-wide/InvoicePanel.vue'
import PaymentPanel from './register-wide/PaymentPanel.vue'

// Composables
import { useProductCache } from './register-wide/composables/useProductCache'
import { useInvoiceCalculations } from './register-wide/composables/useInvoiceCalculations'

export default {
  name: 'PanelRowsWide',

  components: {
    ProductGrid,
    InvoicePanel,
    PaymentPanel,
    ShowError: () => import('@/components/ShowError'),
    TilePanelDialog: () => import('@/modules/cash-register/components/dialogs/TilePanelDialog'),
    SelectProductFromCategory: () => import('@/modules/cash-register/components/dialogs/SelectProductFromCategory'),
    MenuDialog: () => import('@/modules/cash-register/components/MenuDialog'),
    AddDiscountForm: () => import('./receipt-calculator-steps/AddDiscountForm.vue'),
    SearchItemsDialog: () => import('@/modules/cash-register/components/dialogs/SearchItemsDialog'),
    ReceiptsDialog: () => import('@/modules/cash-register/components/dialogs/ReceiptsDialog'),
    CloseBalance: () => import('@/modules/cash-register/components/closeBalance/CloseBalance'),
    AddNoteDialog: () => import('@/modules/cash-register/components/dialogs/AddNoteDialog'),
    ViewTableNotesDialog: () => import('@/modules/cash-register/components/dialogs/ViewTableNotesDialog'),
    AddNoteArticleDialog: () => import('@/modules/cash-register/components/dialogs/AddNoteArticleDialog'),
    AddManualPrice: () => import('@/modules/cash-register/components/AddManualPrice'),
    DiscountSelectionDialog: () => import('@/modules/cash-register/components/dialogs/DiscountSelectionDialog'),
    ItemDiscountSelectionDialogForSmallScreen: () => import('@/modules/cash-register/components/dialogs/ItemDiscountSelectionDialogForSmallScreen')
  },

  inject: ['showMsgBox', 'hideLoader', 'confirm', 'confirmClose'],

  mixins: [onScreenKeyboard],

  props: {
    categories: {
      type: Array,
      default: () => []
    },
    selectedPrinter: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      // Dialog states
      addItemDialogOpen: false,
      editItemDialogOpen: false,
      selectProductDialogOpen: false,
      searchItemsDialogOpen: false,
      receiptsDialogOpen: false,

      // Core state
      cashReg: {},
      selectedTile: undefined,
      selectedCategoryId: undefined,
      selectedCategoryName: undefined,
      invoice: {},
      selectedTable: {},
      editMode: false,
      ordersView: false,
      editItem: {},
      currency: 'EUR',
      generalMenu: [],
      lastInsertBy: '',
      cartConfiguration: undefined,
      keyboardShortcutsEnabled: false,
      showShortcutBadges: false,
      closeModalOnProductClick: false,
      categoryDisplayMode: 'tiles', // 'tiles' or 'rows'

      // Quantity Bar settings
      quantityBarEnabled: false,
      quantityBarAutoReset: true,
      selectedQuantity: 1,

      // Barcode scanner
      barcode: [],
      barcodeTimer: null,
      useBarcodeScanner: false,
      boundBarcodeHandler: null,

      // Composable instances
      productCacheComposable: null,
      invoiceCalculationsComposable: null,

      // EventBus cleanup functions
      eventBusCleanups: [],

      // ResizeObserver for product grid height
      productGridResizeObserver: null,

      // Pending quantities tracking (not yet saved)
      itemPendingQuantities: {},
      transactionRedirectTimer: null
    }
  },

  computed: {
    total () {
      return this.invoice.total
    },
    totalDiscount () {
      return this.invoice.fullTotal - this.invoice.total
    },
    // Map item IDs to their quantities in cart
    itemQuantities () {
      const quantities = {}
      if (this.invoice && this.invoice.items) {
        this.invoice.items.forEach(item => {
          if (item && item.id) {
            quantities[item.id] = (quantities[item.id] || 0) + (item.quantity || 1)
          }
        })
      }
      return quantities
    }
  },

  watch: {
    'invoice.discount': {
      handler () {
        this.updateGeneralMenu()
      }
    },
    'invoice.items': {
      handler () {
        if (this.invoiceCalculationsComposable) {
          this.invoiceCalculationsComposable.recalculateRows()
        }
      }
    }
  },

  methods: {
    // Initialize composables (Vue 2 pattern)
    initComposables () {
      // Product cache composable
      this.productCacheComposable = useProductCache()

      // Invoice calculations composable
      this.invoiceCalculationsComposable = useInvoiceCalculations(this)
    },

    // Load cart configuration from Firebase
    async loadConfiguration () {
      try {
        const conf = await df.doc(`cash_registers/${state.getCashRegister().id}/cart_configuration/configuration`).get()

        if (conf && conf.data()) {
          this.cartConfiguration = conf.data()
        } else {
          // Default configuration - show all columns
          this.cartConfiguration = {
            rb: true,
            name: true,
            quantity: true,
            pending_quantity: false,
            unit: true,
            single_price: true,
            single_price_discount: true,
            total: true
          }
        }
      } catch (err) {
        console.error('Error loading cart configuration:', err)
        // Set default on error
        this.cartConfiguration = {
          rb: true,
          name: true,
          quantity: true,
          pending_quantity: false,
          unit: true,
          single_price: true,
          single_price_discount: true,
          total: true
        }
      }
    },

    // Keyboard shortcuts settings
    loadKeyboardSettings () {
      try {
        const key = `keyboard_shortcuts_${state.getCashRegister().id}`
        const saved = localStorage.getItem(key)
        if (saved) {
          const settings = JSON.parse(saved)
          this.keyboardShortcutsEnabled = settings.enabled !== undefined ? settings.enabled : false
          this.showShortcutBadges = settings.showBadges !== undefined ? settings.showBadges : false
        }
      } catch (err) {
        console.error('Error loading keyboard settings:', err)
      }
    },

    // Barcode scanner methods
    validateBarcode (b) {
      const Barcoder = require('barcoder')
      return Barcoder.validate(b)
    },

    barcodeFind (e) {
      // Ignoriši specijalne tastere (Shift, Control, Alt, itd.)
      const ignoredKeys = [
        'Shift', 'Control', 'Alt', 'Meta',
        'CapsLock', 'Tab', 'Escape',
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown',
        'Insert', 'Delete', 'Backspace',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6',
        'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]

      if (ignoredKeys.includes(e.key)) {
        return
      }

      if (e.key !== 'Enter') {
        if (e.key.length === 1) {
          this.barcode.push(e.key)
        }
      }

      if (this.barcodeTimer) {
        clearTimeout(this.barcodeTimer)
      }

      this.barcodeTimer = setTimeout(() => {
        const barcodeString = this.barcode.join('')

        if (this.validateBarcode(barcodeString)) {
          this.productCacheComposable.getProductByBarcode(
            state.getPointOfSale().id,
            barcodeString,
            this.currency
          )
            .then((product) => {
              if (product) {
                let it = product

                it.stock = it.stock / this.multiplier

                if (it.warehouse_units !== undefined && it.warehouse_units.units !== undefined) {
                  Object.keys(it.warehouse_units.units).forEach(key => {
                    if (it.warehouse_units.units[key].prices !== undefined) {
                      let newIt = {}
                      newIt = clone(it)
                      newIt.prices = it.warehouse_units.units[key].prices

                      var stUnit = it.warehouse_units.units[key].storage_unit
                      if (stUnit === '.kom') {
                        stUnit = ''
                      }

                      newIt.name = it.name + ' ' + stUnit
                      newIt.id = newIt.id + '.' + it.warehouse_units.units[key].id
                      it = newIt
                    }
                  })
                }

                it.type = 'barcode-added'
                this.addProduct(it, 1) // Barcode scanner uvijek dodaje 1, ignoriraj selectedQuantity
              } else {
                this.showMsgBox({
                  text: `Proizvod sa barkodom ${barcodeString} nije pronađen`,
                  title: 'Proizvod nije pronađen',
                  actions: ['cancel'],
                  cancelBtnText: 'U redu',
                  color: 'info'
                })
              }
            })
            .catch(err => {
              console.error('Barcode scanner cache lookup error:', err)
            })
        }

        this.barcode = []
        this.barcodeTimer = null
      }, 100)
    },

    handleKeyEvents (e) {
      e = e || window.event
      this.barcodeFind(e)
    },

    loadBarcodeSetting () {
      try {
        const key = `use_barcode_scanner_${state.getCashRegister().id}`
        const saved = localStorage.getItem(key)
        if (saved !== null) {
          this.useBarcodeScanner = saved === 'true'
        }
      } catch (err) {
        console.error('Error loading barcode scanner setting:', err)
      }
    },

    loadModalCloseSetting () {
      try {
        const key = `close_modal_on_product_click_${state.getCashRegister().id}`
        const saved = localStorage.getItem(key)
        if (saved !== null) {
          this.closeModalOnProductClick = saved === 'true'
        }
      } catch (err) {
        console.error('Error loading modal close setting:', err)
      }
    },

    loadCategoryDisplayMode () {
      try {
        const key = `category_display_mode_${state.getCashRegister().id}`
        const saved = localStorage.getItem(key)
        this.categoryDisplayMode = saved || 'tiles'
      } catch (err) {
        console.error('Error loading category display mode:', err)
        this.categoryDisplayMode = 'tiles'
      }
    },

    loadQuantityBarSetting () {
      try {
        const enabledKey = `quantity_bar_enabled_${state.getCashRegister().id}`
        const autoResetKey = `quantity_bar_auto_reset_${state.getCashRegister().id}`

        const savedEnabled = localStorage.getItem(enabledKey)
        const savedAutoReset = localStorage.getItem(autoResetKey)

        if (savedEnabled !== null) {
          this.quantityBarEnabled = savedEnabled === 'true'
        }
        if (savedAutoReset !== null) {
          this.quantityBarAutoReset = savedAutoReset === 'true'
        } else {
          this.quantityBarAutoReset = true
        }
      } catch (err) {
        console.error('Error loading quantity bar setting:', err)
      }
    },

    // General menu
    updateGeneralMenu () {
      this.generalMenu = [
        { text: 'Ukloni artikle', method: 'removeProducts', icon: 'mdi-delete' },
        { text: 'Dodaj napomenu', method: 'addNoteDialogOpen', icon: 'mdi-delete' }
      ]
    },

    // Product operations
    async addProduct (item, quantity = this.selectedQuantity) {
      if (item.type === 'menu-product') {
        const product = await df.doc(`location_menu_pricelist/${state.getPointOfSale().id}/items/${item.id}`).get()
        const invoiceItem = product.data()
        this.$refs.menuDialog.open(invoiceItem, this.currency)
        return
      } else if (item.type === 'barcode-added') {
        const id = item.id
        let unitId = ''

        if (id.includes('.')) {
          unitId = id.split('.')[1]
        }

        let invoiceItem = item
        invoiceItem.total = 0

        let existingInvoiceItem = this.invoice.items.find(p => p.id === item.id)

        if (invoiceItem.warehouse_units !== undefined && invoiceItem.warehouse_units.units !== undefined) {
          Object.keys(invoiceItem.warehouse_units.units).forEach(key => {
            if (key === unitId && invoiceItem.warehouse_units.units[key].prices !== undefined) {
              let newIt = {}
              newIt = clone(invoiceItem)
              newIt.prices = invoiceItem.warehouse_units.units[key].prices
              newIt.name = invoiceItem.name
              invoiceItem = newIt
            }
          })
        }

        if (existingInvoiceItem) {
          const itemIndex = this.invoice.items.findIndex(p => p.id === item.id)

          if (this.cashReg.type === 'VP') {
            existingInvoiceItem = this.getWholesaleItem(existingInvoiceItem)
          }
          const newQuantity = existingInvoiceItem.quantity + quantity

          // Create a new object with updated properties (don't mutate the existing object)
          const fullPrice = existingInvoiceItem.prices[this.currency].price
          const effectivePrice = existingInvoiceItem.discountedUnitPrice !== undefined ? existingInvoiceItem.discountedUnitPrice : fullPrice
          const updatedItem = {
            ...existingInvoiceItem,
            quantity: newQuantity,
            unitPrice: fullPrice,
            discountedUnitPrice: effectivePrice,
            total: effectivePrice * newQuantity
          }

          // Replace the item in the array using Vue.set for reactivity
          this.$set(this.invoice.items, itemIndex, updatedItem)

          // Track pending quantity for existing item
          if (!this.itemPendingQuantities[item.id]) {
            this.itemPendingQuantities[item.id] = 0
          }
          this.itemPendingQuantities[item.id] += quantity

          // Trigger recalculation to update UI
          if (this.invoiceCalculationsComposable) {
            this.invoiceCalculationsComposable.recalculate()
          }
        } else {
          // Set organizational_unit_id for barcode-added items
          if (invoiceItem.org_units && invoiceItem.org_units[state.getPointOfSale().id]) {
            invoiceItem.organizational_unit_id = invoiceItem.org_units[state.getPointOfSale().id]
          }

          if (this.cashReg.type === 'VP') {
            invoiceItem = this.getWholesaleItem(invoiceItem)
          }
          invoiceItem.quantity = quantity
          invoiceItem.unitPrice = invoiceItem.prices[this.currency].price
          invoiceItem.discountedUnitPrice = invoiceItem.unitPrice
          invoiceItem.total = invoiceItem.unitPrice * quantity
          this.invoice.items.unshift(invoiceItem)

          // Initialize pending quantity for new item
          this.itemPendingQuantities[invoiceItem.id] = quantity
        }
      } else {
        if (item.id.includes('.')) {
          const ids = item.id.split('.')
          item.id = ids[0]
          item.unitId = ids[1]
        }

        let invoiceItem = await this.productCacheComposable.getProductFromCache(state.getPointOfSale().id, item.id)
        if (item.unitId && item.unitId !== '') { item.id = item.id + '.' + item.unitId }

        if (item.unitId && item.unitId !== '') {
          if (invoiceItem.warehouse_units && invoiceItem.warehouse_units.units && invoiceItem.warehouse_units.units[item.unitId].prices) {
            let newProduct = {}
            newProduct = clone(invoiceItem)
            newProduct.prices = invoiceItem.warehouse_units.units[item.unitId].prices

            var stUnit = invoiceItem.warehouse_units.units[item.unitId].storage_unit
            if (stUnit === '.kom') {
              stUnit = ''
            }
            if (invoiceItem.warehouse_units.units[item.unitId].base_unit === true) {
              stUnit = invoiceItem.selling_unit
            }
            newProduct.name = invoiceItem.name + ' ' + stUnit
            newProduct.id = newProduct.id + '.' + item.unitId
            invoiceItem = newProduct
          }
        }

        invoiceItem.menu = [
          { text: 'Dodaj popust', method: 'addDiscount', icon: 'mdi-percent' },
          { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' },
          { text: 'Dodaj napomenu', method: 'addNoteArticleDialogOpen' }
        ]
        invoiceItem.total = 0

        // Set organizational_unit_id from product's org_units for current location
        if (invoiceItem.org_units && invoiceItem.org_units[state.getPointOfSale().id]) {
          invoiceItem.organizational_unit_id = invoiceItem.org_units[state.getPointOfSale().id]
        }

        if (this.cashReg.type === 'VP') {
          invoiceItem = this.getWholesaleItem(invoiceItem)
        }

        const existingInvoiceItem = this.invoice.items.find(p => p.id === invoiceItem.id)
        if (existingInvoiceItem) {
          const newQuantity = existingInvoiceItem.quantity + quantity
          existingInvoiceItem.quantity = newQuantity
          existingInvoiceItem.unitPrice = existingInvoiceItem.prices[this.currency].price
          existingInvoiceItem.discountedUnitPrice = existingInvoiceItem.discountedUnitPrice !== undefined ? existingInvoiceItem.discountedUnitPrice : existingInvoiceItem.unitPrice
          existingInvoiceItem.total = existingInvoiceItem.discountedUnitPrice * newQuantity
          // Track pending quantity for existing item
          if (!this.itemPendingQuantities[invoiceItem.id]) {
            this.itemPendingQuantities[invoiceItem.id] = 0
          }
          this.itemPendingQuantities[invoiceItem.id] += quantity
        } else {
          if (invoiceItem.manual_price_input) {
            invoiceItem.currency = this.currency
            invoiceItem.quantity = quantity
            this.$refs.addManualPrice.open(invoiceItem)
            return
          }
          invoiceItem.quantity = quantity
          invoiceItem.unitPrice = invoiceItem.prices[this.currency].price
          invoiceItem.discountedUnitPrice = invoiceItem.unitPrice
          invoiceItem.total = invoiceItem.unitPrice * quantity
          this.invoice.items.unshift(invoiceItem)
          const rbr = this.invoice.items.findIndex(p => p.id === item.id) + 1
          this.invoice.items.find(p => p.id === item.id).rbr = rbr
          // Initialize pending quantity for new item
          this.itemPendingQuantities[invoiceItem.id] = quantity
        }
      }

      // Auto-reset logika
      if (this.quantityBarEnabled && this.quantityBarAutoReset) {
        this.selectedQuantity = 1
      }

      this.recalculate()
    },

    // Set product quantity directly (like EditReceiptDialog does)
    async setProductQuantity (item, qty) {
      // First, ensure we have the full product data
      if (item.id.includes('.')) {
        const ids = item.id.split('.')
        item.id = ids[0]
        item.unitId = ids[1]
      }

      let invoiceItem = await this.productCacheComposable.getProductFromCache(state.getPointOfSale().id, item.id)
      if (item.unitId && item.unitId !== '') { item.id = item.id + '.' + item.unitId }

      if (item.unitId && item.unitId !== '') {
        if (invoiceItem.warehouse_units && invoiceItem.warehouse_units.units && invoiceItem.warehouse_units.units[item.unitId].prices) {
          let newProduct = {}
          newProduct = clone(invoiceItem)
          newProduct.prices = invoiceItem.warehouse_units.units[item.unitId].prices

          var stUnit = invoiceItem.warehouse_units.units[item.unitId].storage_unit
          if (stUnit === '.kom') {
            stUnit = ''
          }
          if (invoiceItem.warehouse_units.units[item.unitId].base_unit === true) {
            stUnit = invoiceItem.selling_unit
          }
          newProduct.name = invoiceItem.name + ' ' + stUnit
          newProduct.id = newProduct.id + '.' + item.unitId
          invoiceItem = newProduct
        }
      }

      invoiceItem.menu = [
        { text: 'Dodaj popust', method: 'addDiscount', icon: 'mdi-percent' },
        { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' },
        { text: 'Dodaj napomenu', method: 'addNoteArticleDialogOpen' }
      ]
      invoiceItem.total = 0

      if (this.cashReg.type === 'VP') {
        invoiceItem = this.getWholesaleItem(invoiceItem)
      }

      // Find existing item in cart
      const existingInvoiceItem = this.invoice.items.find(p => p.id === invoiceItem.id)

      if (existingInvoiceItem) {
        // Item exists - set quantity directly (don't add, just set)
        existingInvoiceItem.quantity = qty
        existingInvoiceItem.unitPrice = existingInvoiceItem.prices[this.currency].price
        existingInvoiceItem.discountedUnitPrice = existingInvoiceItem.discountedUnitPrice !== undefined ? existingInvoiceItem.discountedUnitPrice : existingInvoiceItem.unitPrice
        existingInvoiceItem.total = existingInvoiceItem.discountedUnitPrice * qty
      } else {
        // Item doesn't exist - add it with the specified quantity
        if (invoiceItem.manual_price_input) {
          invoiceItem.currency = this.currency
          this.$refs.addManualPrice.open(invoiceItem)
          return
        }
        invoiceItem.quantity = qty
        invoiceItem.unitPrice = invoiceItem.prices[this.currency].price
        invoiceItem.discountedUnitPrice = invoiceItem.unitPrice
        invoiceItem.total = invoiceItem.unitPrice * qty
        this.invoice.items.unshift(invoiceItem)
        const rbr = this.invoice.items.findIndex(p => p.id === item.id) + 1
        this.invoice.items.find(p => p.id === item.id).rbr = rbr
      }

      this.recalculate()
    },

    handleModifyProduct (item, operation) {
      // Find existing item in cart
      const existingItem = this.invoice.items.find(p => p.id === item.id)

      if (operation === 'add') {
        // Reuse existing addProduct logic
        this.addProduct(item)
      } else if (operation === 'decrement' && existingItem) {
        // Reuse existing onRowBtnClick logic
        this.onRowBtnClick(existingItem, 'decrement')
      } else if (operation === 'removeAll' && existingItem) {
        // Reuse existing onRowBtnClick logic
        this.onRowBtnClick(existingItem, 'remove')
      }
    },

    addMenuProduct (invoiceItem) {
      const existingInvoiceItem = this.invoice.items.find(p => p.id === invoiceItem.id)
      if (existingInvoiceItem) {
        const quantity = existingInvoiceItem.quantity + 1
        existingInvoiceItem.quantity = quantity
        existingInvoiceItem.unitPrice = existingInvoiceItem.prices[this.currency].price
        existingInvoiceItem.discountedUnitPrice = existingInvoiceItem.discountedUnitPrice !== undefined ? existingInvoiceItem.discountedUnitPrice : existingInvoiceItem.unitPrice
        existingInvoiceItem.total = existingInvoiceItem.discountedUnitPrice * quantity
        existingInvoiceItem.type = 'menu-product'

        if (invoiceItem.choosenProductOrder) {
          Object.keys(invoiceItem.choosenProductOrder).forEach(key => {
            if (!existingInvoiceItem.choosenProductOrder) {
              existingInvoiceItem.choosenProductOrder = {}
            }
            existingInvoiceItem.choosenProductOrder[key] = invoiceItem.choosenProductOrder[key]

            if (!existingInvoiceItem.newKeys) {
              existingInvoiceItem.newKeys = {}
            }
            existingInvoiceItem.newKeys[key] = {}
          })
        }

        if (invoiceItem.choosenTagOrder) {
          Object.keys(invoiceItem.choosenTagOrder).forEach(key => {
            if (!existingInvoiceItem.choosenTagOrder) {
              existingInvoiceItem.choosenTagOrder = {}
            }
            existingInvoiceItem.choosenTagOrder[key] = invoiceItem.choosenTagOrder[key]

            if (!existingInvoiceItem.newKeys) {
              existingInvoiceItem.newKeys = {}
            }
            existingInvoiceItem.newKeys[key] = {}
          })
        }

        this.$set(this.invoice, 'items', [...this.invoice.items])
      } else {
        invoiceItem.total = 0
        invoiceItem.quantity = 1
        invoiceItem.unitPrice = invoiceItem.prices[this.currency].price
        invoiceItem.total = invoiceItem.unitPrice
        invoiceItem.discountedUnitPrice = invoiceItem.unitPrice
        invoiceItem.type = 'menu-product'
        invoiceItem.menu = [
          { text: 'Dodaj popust', method: 'addDiscount', icon: 'mdi-percent' },
          { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' },
          { text: 'Ukloni artikl', method: 'addNoteDialogOpen' }
        ]

        if (invoiceItem.choosenProductOrder) {
          Object.keys(invoiceItem.choosenProductOrder).forEach(key => {
            if (!invoiceItem.newKeys) {
              invoiceItem.newKeys = {}
            }
            invoiceItem.newKeys[key] = {}
          })
        }

        if (invoiceItem.choosenTagOrder) {
          Object.keys(invoiceItem.choosenTagOrder).forEach(key => {
            if (!invoiceItem.newKeys) {
              invoiceItem.newKeys = {}
            }
            invoiceItem.newKeys[key] = {}
          })
        }

        this.invoice.items.push(invoiceItem)
        const rbr = this.invoice.items.findIndex(p => p.id === invoiceItem.id) + 1
        this.invoice.items.find(p => p.id === invoiceItem.id).rbr = rbr
      }
      this.recalculate()
    },

    // Invoice row operations
    onRowBtnClick (item, operation) {
      const idx = this.invoice.items.findIndex(p => p.id === item.id)
      switch (operation) {
        case 'increment':
          item.quantity = Number(item.quantity) + 1
          // Track pending quantity
          if (!this.itemPendingQuantities[item.id]) {
            this.itemPendingQuantities[item.id] = 0
          }
          this.itemPendingQuantities[item.id]++
          break
        case 'decrement':
          if (item.quantity === 1) {
            this.invoice.items.splice(idx, 1)
            // Remove pending tracking for deleted item
            delete this.itemPendingQuantities[item.id]
          } else if (item.quantity > 1) {
            item.quantity = Number(item.quantity) - 1
            // Decrease pending quantity (but not below 0)
            if (this.itemPendingQuantities[item.id] && this.itemPendingQuantities[item.id] > 0) {
              this.itemPendingQuantities[item.id]--
            }
          }
          break
        case 'discount':
          if (item.discountedUnitPrice === undefined || item.discountedUnitPrice === 0) {
            this.addDiscount(false, item.id)
          } else {
            this.removeDiscount(false, item.id)
          }
          break
        case 'remove':
          this.invoice.items.splice(idx, 1)
          // Remove pending tracking for deleted item
          delete this.itemPendingQuantities[item.id]
          break
        default:
          break
      }
      if (this.invoice.items.length === 0) {
        this.invoice.total = 0
        this.invoice.fullTotal = 0
        this.invoice.totalWithDiscount = 0
        this.invoice.totalShoppingCart = 0
        this.invoice.discount = undefined
        EventBus.$emit('clear-table')
      }
      this.recalculate()
    },

    onChangeQuantity (item) {
      const idx = this.invoice.items.findIndex(it => it?.id === item.id)
      if (idx > -1) {
        this.invoice.items[idx] = item
      }
      this.recalculate()
    },

    onNameClick (item) {
      // Handle name editing if needed
    },

    // Calculation methods (delegated to composable)
    recalculate () {
      if (this.invoiceCalculationsComposable) {
        this.invoiceCalculationsComposable.recalculate()
      }
    },

    // Menu actions
    generalMenuClick (method) {
      switch (method) {
        case 'removeProducts':
          this.removeProducts()
          break
        case 'addNoteDialogOpen':
          this.addNoteDialogOpen()
          break
      }
    },

    itemMenuClick (method, item) {
      switch (method) {
        case 'addDiscount':
          this.addDiscount(false, item.id)
          break
        case 'removeDiscount':
          this.removeDiscount(false, item.id)
          break
        case 'removeItem':
          this.onRowBtnClick(item, 'remove')
          break
        case 'addNoteArticleDialogOpen':
          this.addNoteArticleDialogOpen(item)
          break
        case 'increment':
          this.onRowBtnClick(item, method)
          break
        case 'decrement':
          this.onRowBtnClick(item, method)
          break
      }
    },

    removeProducts () {
      // ✅ FIX #7: Zaštita - pitaj korisnika prije brisanja!
      if (!this.invoice.items || this.invoice.items.length === 0) {
        return // Nema artikala za brisati
      }

      // Provjeri da li je stol odabran i ima li nespremljenih artikala
      const hasUnsavedItems = this.selectedTable && this.selectedTable.id && this.invoice.items.length > 0

      this.confirm({
        title: 'Brisanje svih artikala',
        message: hasUnsavedItems
          ? `Stol ${this.selectedTable.name || ''} ima ${this.invoice.items.length} artikala. Jeste li sigurni da želite obrisati SVE artikle?`
          : `Imate ${this.invoice.items.length} artikala u košarici. Jeste li sigurni da želite obrisati SVE?`,
        options: {
          toolbar: true,
          width: 450,
          confirmText: 'DA, obriši sve',
          cancelText: 'Odustani'
        }
      }).then((resp) => {
        if (resp) {
          // Korisnik je potvrdio brisanje
          this.invoice.items = []
          this.invoice.total = 0
          this.invoice.fullTotal = 0
          this.invoice.totalWithDiscount = 0
          this.invoice.totalShoppingCart = 0
          this.invoice.discount = undefined
          // Reset pending quantities
          this.itemPendingQuantities = {}
          EventBus.$emit('clear-table')
        }
        this.confirmClose()
      })
    },

    // Discount operations
    addDiscount (mainBasket = false, itemID) {
      this.$refs.addDiscountForm.open(mainBasket, itemID, this.currency)
    },

    onCartDiscountClick (hasDiscounts) {
      if (hasDiscounts) {
        // Remove discounts from all items
        this.removeAllDiscounts()
      } else {
        // Open discount dialog for all items
        this.$refs.addDiscountForm.open(false, 'ALL_ITEMS', this.currency)
      }
    },

    removeAllDiscounts () {
      if (!this.invoice || !this.invoice.items) return

      this.invoice.items.forEach(item => {
        if (item.discount) {
          delete item.discount
          // Recalculate item total without discount
          item.discountedUnitPrice = item.unitPrice
          item.total = item.unitPrice * item.quantity
          // Reset menu
          item.menu = [
            { text: 'Dodaj popust', method: 'addDiscount', icon: 'mdi-percent' },
            { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' },
            { text: 'Dodaj napomenu', method: 'addNoteArticleDialogOpen' }
          ]
        }
      })

      // Recalculate totals
      this.recalculate()
    },

    applyDiscountToItem (item, discount) {
      // Clone discount for this item
      item.discount = { ...discount }

      switch (discount.type) {
        case 'RATE': {
          const temp = Math.round((item.prices[this.currency].price) - ((item.prices[this.currency].price * (discount.rate / 10000))))
          item.discounted_price = temp
          item.discount.amount = item.prices[this.currency].price - item.discounted_price
          item.discountedUnitPrice = item.discounted_price

          if (item.discounted_price <= 0) {
            item.discount = undefined
            item.discounted_price = 0
            this.showMsgBox({ text: `Popust je veći od cijene artikla "${item.name}", te se ne može primjeniti.`, actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
          }
          break
        }
        case 'AMOUNT':
          item.discounted_price = Math.round(item.prices[this.currency].price - discount.amount)
          item.discount.rate = ((discount.amount / item.prices[this.currency].price) * 100) * 100
          item.discountedUnitPrice = item.discounted_price
          if (item.discounted_price <= 0) {
            item.discount = undefined
            item.discounted_price = 0
            item.discountedUnitPrice = item.discounted_price
            this.showMsgBox({ text: `Popust je veći od cijene artikla "${item.name}", te se ne može primjeniti.`, actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
          }
          break
      }

      // Update menu to show remove discount option
      if (item.discount) {
        item.menu = [
          { text: 'Ukloni popust', method: 'removeDiscount', icon: 'mdi-delete' },
          { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' }
        ]
      }
    },

    onAddDiscount (discount) {
      if (discount !== undefined) {
        if (discount.basketDiscount === true) {
          switch (discount.type) {
            case 'RATE': {
              const temp = ((this.invoice.total) * (discount.rate / 10000))
              this.invoice.discount = discount
              this.invoice.discount.amount = temp
              break
            }
            case 'AMOUNT':
              this.invoice.discount = discount
              this.invoice.discount.rate = (discount.amount / this.invoice.total) * 100
              break
          }
        } else if (discount.itemID === 'ALL_ITEMS') {
          // Apply discount to ALL items in cart
          this.invoice.items.forEach((item, key) => {
            this.applyDiscountToItem(this.invoice.items[key], discount)
          })
        } else {
          // Apply discount to single item
          this.invoice.items.forEach((item, key) => {
            if (item.id === discount.itemID) {
              this.applyDiscountToItem(this.invoice.items[key], discount)
            }
          })
        }
        this.recalculate()
      }
    },

    removeDiscount (basketDiscount = false, itemID) {
      if (basketDiscount) {
        this.invoice.totalWithDiscount = 0
        this.invoice.discount = undefined
      } else {
        this.invoice.items.forEach((item, key) => {
          if (item.id === itemID) {
            this.invoice.items[key].discounted_price = this.invoice.items[key].unitPrice
            this.invoice.items[key].discountedUnitPrice = this.invoice.items[key].unitPrice

            this.invoice.items[key].menu = [
              { text: 'Dodaj popust', method: 'addDiscount', icon: 'mdi-percent' },
              { text: 'Ukloni artikl', method: 'removeItem', icon: 'mdi-delete' }
            ]
          }
        })
      }
      this.recalculate()
    },

    openDiscountSelectionDialog () {
      this.$refs.discountSelectionDialog.open()
    },

    openItemsDiscountSelectionDialog (item) {
      this.$refs.itemDiscountSelectionDialog.open(item)
    },

    // Panel item operations
    onCellClick (item) {
      this.selectedTile = item
      if (this.editMode) {
        if (item.active) {
          this.editPanelItem(item)
        } else {
          this.addPanelItem(item)
        }
      } else {
        if (item.type === 'category') {
          this.selectedCategoryId = item.id
          this.selectedCategoryName = item.label
          this.selectProductDialogOpen = true
        } else {
          this.addProduct(item)
        }
      }
    },

    async onTileUpdated (tile) {
      // Save tile data to Firebase
      if (!tile || !this.$refs.productGrid) {
        console.error('onTileUpdated: tile or productGrid ref is missing', { tile, ref: this.$refs.productGrid })
        return
      }

      // Use $nextTick to ensure DOM and refs are updated
      await this.$nextTick()

      const rows = this.$refs.productGrid.rows

      // Support both row_index and rowIndex properties
      const rowIndex = tile.rowIndex ?? tile.row_index

      if (!rows || !rows[rowIndex]) {
        console.error('onTileUpdated: rows array not available', {
          rows: rows ? 'exists' : 'null',
          rowsLength: rows?.length,
          rowIndex,
          tile
        })
        return
      }

      // Sanitize cols array for Firebase - remove undefined and extra fields
      const sanitizedCols = rows[rowIndex].cols.map(col => ({
        active: col.active ?? false,
        row_index: col.rowIndex ?? col.row_index ?? 0,
        col_index: col.colIndex ?? col.col_index ?? 0,
        color: col.color ?? 'primary',
        foregroundColor: col.foregroundColor ?? 'white',
        colspan: col.colspan ?? 1,
        rowspan: col.rowspan ?? 1,
        id: col.id ?? '',
        parent_id: col.parentId ?? col.parent_id ?? '',
        label: col.label ?? '',
        type: col.type ?? '',
        merged_into: col.mergedInto ?? col.merged_into ?? null,
        hasGlassEffect: col.hasGlassEffect ?? false,
        has3DEffect: col.has3DEffect ?? false,
        hasNeonEffect: col.hasNeonEffect ?? false,
        hasPulseEffect: col.hasPulseEffect ?? false,
        hasNeumorphismEffect: col.hasNeumorphismEffect ?? false,
        hasGradientBorderEffect: col.hasGradientBorderEffect ?? false,
        hasShimmerEffect: col.hasShimmerEffect ?? false,
        hasFloatingEffect: col.hasFloatingEffect ?? false,
        hasShadowLiftEffect: col.hasShadowLiftEffect ?? false,
        hasHolographicEffect: col.hasHolographicEffect ?? false,
        hasFrostEffect: col.hasFrostEffect ?? false,
        hasBorderGlowEffect: col.hasBorderGlowEffect ?? false,
        hasBreathingEffect: col.hasBreathingEffect ?? false
      }))

      const docRef = df.doc(`cash_registers/${state.getCashRegister().id}/gui_configuration_rows/row${rowIndex}`)
      try {
        await docRef.set({
          cols: sanitizedCols
        })
      } catch (err) {
        console.error('Error updating tile: ', err)
      }
    },

    editPanelItem (item) {
      this.editItem = item
      this.editItemDialogOpen = true
    },

    addPanelItem (item) {
      if (item) {
        this.selectedTile = item
      }
      this.addItemDialogOpen = true
    },

    onAddProductToPanel (product, category, colspan, backgroundColor, foregroundColor, effects) {
      const type = product.products ? 'menu-product' : 'product'

      // Call ProductGrid's addItemToPanel method via ref
      if (this.$refs.productGrid) {
        this.$refs.productGrid.addItemToPanel(
          product.id,
          category.id,
          colspan,
          product.name,
          backgroundColor,
          foregroundColor,
          type,
          this.selectedTile,
          effects
        )
      }

      this.addItemDialogOpen = false
    },

    onAddCategoryToPanel (category, colspan, backgroundColor, foregroundColor, effects) {
      // Call ProductGrid's addItemToPanel method via ref
      if (this.$refs.productGrid) {
        this.$refs.productGrid.addItemToPanel(
          category.id,
          '', // no parent for category
          colspan,
          category.name,
          backgroundColor,
          foregroundColor,
          'category',
          this.selectedTile,
          effects
        )
      }

      this.addItemDialogOpen = false
    },

    onChangeCategoryOnPanel (item, colspan, color, foregroundColor, effects) {
      // Call ProductGrid's addItemToPanel method via ref
      if (this.$refs.productGrid) {
        this.$refs.productGrid.addItemToPanel(
          item.id,
          '', // no parent for category
          colspan,
          item.label,
          color,
          foregroundColor,
          'category',
          this.editItem, // use editItem as the selectedTile
          effects
        )
      }

      this.editItemDialogOpen = false
    },

    onChangeProductOnPanel (item, colspan, color, foregroundColor, effects) {
      const type = item.type || 'product'

      // Call ProductGrid's addItemToPanel method via ref
      if (this.$refs.productGrid) {
        this.$refs.productGrid.addItemToPanel(
          item.id,
          item.parent_id,
          colspan,
          item.label,
          color,
          foregroundColor,
          type,
          this.editItem, // use editItem as the selectedTile
          effects
        )
      }

      this.editItemDialogOpen = false
    },

    async onDeleteItem (item) {
      this.editItemDialogOpen = false

      // Call ProductGrid's onDeleteItem method to delete the tile
      if (this.$refs.productGrid) {
        await this.$refs.productGrid.onDeleteItem(item)
      }
    },

    // Dialog handlers
    onAddItemDialogClose () {
      this.addItemDialogOpen = false
    },

    onEditItemDialogClose () {
      this.editItemDialogOpen = false
    },

    onAddProductDialogClose () {
      this.selectProductDialogOpen = false
    },

    onOpenSearchItemsDialog () {
      this.searchItemsDialogOpen = true
    },

    onCloseSearchItemsDialog () {
      this.searchItemsDialogOpen = false
    },

    onOpenReceiptsDialog () {
      this.receiptsDialogOpen = true
    },

    onCloseReceiptsDialog () {
      this.receiptsDialogOpen = false
    },

    onOpenCloseBalanceDialog () {
      this.$refs.closeBalance.open()
    },

    addNoteDialogOpen () {
      this.$refs.addNoteDialog.open(this.selectedTable)
    },

    // NOVO: Otvori dialog s fokusom na stol note
    openTableNoteDialog () {
      this.$refs.addNoteDialog.open(this.selectedTable, {
        focusStolNote: true
      })
    },

    // NOVO: Otvori pregled svih stolnih napomena (badge gumb)
    viewAllTableNotes () {
      this.$refs.viewTableNotesDialog.open(this.selectedTable)
    },

    // NOVO: Handler kad se spremi stolna napomena
    async onTableNoteSaved (unitMessages) {
      // Ažuriraj selectedTable.notes
      if (this.selectedTable) {
        this.$set(this.selectedTable, 'notes', unitMessages)
      }

      // Refresh table data iz Firebasea
      await this.refreshTableData(this.selectedTable.id)
    },

    // NOVO: Refresh table data
    async refreshTableData (tableId) {
      if (!tableId) return

      try {
        const tableDoc = await df.doc(
          `location_orders/${state.getPointOfSale().id}/tables/${tableId}`
        ).get()

        if (tableDoc.exists) {
          const data = tableDoc.data()

          // Ažuriraj samo notes field
          if (this.selectedTable && this.selectedTable.id === tableId) {
            this.$set(this.selectedTable, 'notes', data.notes || [])
          }
        }
      } catch (error) {
        console.error('Error refreshing table data:', error)
      }
    },

    addNoteArticleDialogOpen (item) {
      this.$refs.addNoteArticleDialog.open(item, this.invoice, this.selectedTable)
    },

    onOpenDiscountFromNote (itemId) {
      // Open discount dialog for the item from note dialog
      this.addDiscount(false, itemId)
    },

    onSendNotes (_, order) {
      this.$refs.addNoteDialog.sendNotes(order)
    },

    onResetNodes () {
      this.$refs.addNoteDialog.resetNotes()
    },

    onAddPrice (item) {
      const price = parseFloat(clone(item.price * 100).toFixed(2))
      item.prices[this.currency].price = price

      var newItem = clone(item)
      if (this.cashReg.type === 'VP') {
        newItem.prices[this.currency].price = Math.round(parseFloat(newItem.prices[this.currency].price /
          (1 + newItem.prices[this.currency].taxes[0].rate / 10000)
        ), 2)
      }

      item.quantity = 1
      item.unitPrice = item.prices[this.currency].price
      item.discountedUnitPrice = item.unitPrice
      item.total = item.unitPrice
      this.invoice.items.push(item)
      this.recalculate()
    },

    // Helper function to check if invoice has unsaved changes
    invoiceHasChanges () {
      // Ako nema odabranog stola, provjeri samo da li ima artikala
      if (!this.selectedTable || !this.selectedTable.id) {
        return this.invoice.items && this.invoice.items.length > 0
      }

      // Usporedi invoice.items sa selectedTable.items
      const invoiceItems = this.invoice.items || []
      const tableItems = this.selectedTable.items || []

      // FIX: Ako je invoice PRAZAN, nema novih izmjena (možda je upravo spremljeno)
      if (invoiceItems.length === 0) {
        return false
      }

      // Različit broj artikala = ima promjena
      if (invoiceItems.length !== tableItems.length) {
        return true
      }

      // Ako su objekti IDENTIČNI po referenci, definitivno nema promjena
      if (invoiceItems === tableItems) {
        return false
      }

      // Napravi sortirani string reprezentaciju za usporedbu
      const invoiceSignature = invoiceItems.map(item =>
        `${item.id}:${item.quantity || 1}`
      ).sort().join('|')

      const tableSignature = tableItems.map(item =>
        `${item.id}:${item.quantity || 1}`
      ).sort().join('|')

      if (invoiceSignature === tableSignature) {
        return false
      }

      return true
    },

    // Payment panel handlers
    onSelectTable (table) {
      // ✅ FIX #8: Provjeri da li mijenja na DRUGI stol sa IZMIJENJENIM artiklima
      const isDifferentTable = this.selectedTable && this.selectedTable.id &&
                               table.id && this.selectedTable.id !== table.id

      // Provjeri da li su artikli IZMIJENJENI u odnosu na ono što je spremljeno na stolu
      const hasChanges = this.invoiceHasChanges()

      // Pitaj SAMO ako mijenja na DRUGI stol I ima IZMIJENJENE artikle
      if (isDifferentTable && hasChanges) {
        this.confirm({
          title: 'Promjena stola',
          message: `Trenutno radite na stolu "${this.selectedTable.name}" sa nespremljenim promjenama. Želite li promijeniti na "${table.name || 'novi stol'}"? Nespremljene promjene će biti izgubljene.`,
          options: {
            toolbar: true,
            width: 500,
            confirmText: 'DA, promijeni stol',
            cancelText: 'Odustani'
          }
        }).then((resp) => {
          if (resp) {
            // Korisnik potvrdio
            this.selectedTable = table
            this.invoice.items = [...this.selectedTable.items]
          }
          this.confirmClose()
        })
      } else {
        // Sigurna promjena - ili prvi put odabire stol, ili je isti stol, ili nema promjena
        this.selectedTable = table
        this.invoice.items = [...this.selectedTable.items]
      }
    },

    clearSelection () {
      this.selectedTable = new Table()
      // Reset pending quantities when clearing selection
      this.itemPendingQuantities = {}
    },

    onCreateNewTable (sourceTable, destinationTable) {
      this.selectedTable = sourceTable
      this.invoice.items = sourceTable.items
      this.recalculate()
    },

    onAddWriteInProduct (item) {
      item.currency = this.currency
      const price = parseFloat(item.prices[item.currency].price) * 100
      item.unitPrice = price
      item.prices[this.currency].price = price
      item.quantity = parseFloat(item.quantity)
      item.total = price * item.quantity
      item.fullTotal = price * item.quantity
      item.discountedUnitPrice = price

      item.menu = [
        { text: 'Dodaj popust', method: 'addDiscount' },
        { text: 'Ukloni artikl', method: 'removeItem' },
        { text: 'Dodaj napomenu', method: 'addNoteArticleDialogOpen' }
      ]

      this.invoice.items.push(item)
      this.recalculate()
    },

    onOrdersChange () {
      EventBus.$emit('set-orders-view', !this.ordersView)
    },

    // Utility
    getWholesaleItem (item) {
      const newItem = clone(item)
      if (this.cashReg.type === 'VP') {
        var pnpAmt = 0
        if (newItem.prices[this.currency].taxes.length > 0) {
          newItem.prices[this.currency].taxes.forEach(tax => {
            if (tax.name === 'PNP') {
              pnpAmt = tax.rate
            }
          })
        }

        var taxes = newItem.prices[this.currency].taxes[0].rate + pnpAmt

        newItem.prices[this.currency].price =
          Math.round(parseFloat(newItem.prices[this.currency].price / (1 + taxes / 10000)), 2)
      }
      return newItem
    },

    transactionEndedWithReceipt () {
      if (this.cashReg.after_receipt_behaviour !== undefined && this.cashReg.after_receipt_behaviour !== null && this.cashReg.after_receipt_behaviour !== '') {
        if (this.cashReg.after_receipt_behaviour === 'SHOW_CASH_REGS') {
          if (this.cashReg.login_type === 'SELECT_USER' || this.cashReg.login_type === 'PINCODE') {
            state.setCashRegisterUser({ id: undefined })
            df.doc(`cash_registers/${state.getCashRegister().id}`).update({ reserved_by: '' })
          }
          this.$router.push({
            name: 'pointOfSale.cashRegisters',
            params: {
              companyId: state.getCurrentCompany().id,
              locationId: state.getPointOfSale().id
            }
          })
        } else if (this.cashReg.after_receipt_behaviour === 'SHOW_ALL_RECEIPTS') {
          if (this.cashReg.login_type === 'SELECT_USER' || this.cashReg.login_type === 'PINCODE') {
            state.setCashRegisterUser({ id: undefined })
            df.doc(`cash_registers/${state.getCashRegister().id}`).update({ reserved_by: '' })
          }

          if (this.transactionRedirectTimer) clearTimeout(this.transactionRedirectTimer)
          this.transactionRedirectTimer = setTimeout(() => {
            this.transactionRedirectTimer = null
            this.$router.push({
              name: 'pointOfSale.receipts',
              params: {
                companyId: state.getCurrentCompany().id,
                locationId: state.getPointOfSale().id
              }
            })
          }, 500)
        }
      }
    },

    onClearTable () {
      // FIX: Use invoiceHasChanges() to check for REAL unsaved changes, not just items in invoice
      const hasUnsavedChanges = this.invoiceHasChanges()

      if (hasUnsavedChanges) {
        this.confirm({
          title: 'Čišćenje stola',
          message: `Imate ${this.invoice.items.length} nespremljenih artikala. Jeste li sigurni da želite očistiti stol? Artikli će biti izgubljeni.`,
          options: {
            toolbar: true,
            width: 450,
            confirmText: 'DA, očisti',
            cancelText: 'Odustani'
          }
        }).then((resp) => {
          if (resp) {
            // Korisnik potvrdio
            this.selectedTable = new Table()
            this.invoice.items = []
            // Reset pending quantities
            this.itemPendingQuantities = {}
          }
          this.confirmClose()
        })
      } else {
        // Nema artikala - sigurno čišćenje
        this.selectedTable = new Table()
        this.invoice.items = []
        // Reset pending quantities
        this.itemPendingQuantities = {}
      }
    },

    // Quantity Bar handlers
    onQuantityChanged (quantity) {
      this.selectedQuantity = quantity
    }
  },

  created () {
    // Initialize composables
    this.initComposables()

    // Initialize invoice
    this.selectedTable = new Table()
    this.invoice = {
      currency: this.currency,
      fullTotal: 0,
      items: [],
      paymentConfig: {},
      registerType: 'MP',
      title: '',
      total: 0,
      totalShoppingCart: 0,
      totalWithDiscount: 0,
      type: 'R'
    }

    this.cashReg = state.getCashRegister()

    // Setup barcode handler (removed throttling - was causing character loss)
    // Bind handleKeyEvents to this context for proper event listener cleanup
    this.boundBarcodeHandler = this.handleKeyEvents.bind(this)
  },

  async mounted () {
    // Set currency
    this.currency = 'HRK'
    if (Date.now() / 1000 > 1672527599) {
      this.currency = 'EUR'
    }

    // Load keyboard shortcuts settings
    this.loadKeyboardSettings()

    // Load modal close setting
    this.loadModalCloseSetting()

    // Load category display mode
    this.loadCategoryDisplayMode()

    // Load barcode setting
    this.loadBarcodeSetting()

    // Load quantity bar setting
    this.loadQuantityBarSetting()

    // Load cart configuration
    this.loadConfiguration()

    // Register EventBus listeners
    const listeners = [
      {
        event: 'showError',
        handler: (data) => {
          this.$refs.showError.open(data.message)
        }
      },
      {
        event: 'clear-reload-tables',
        handler: () => {
          this.onClearTable()
        }
      },
      {
        event: 'clear-receipt-data',
        handler: (paymentDone) => {
          EventBus.$emit('clear-table', paymentDone)
          this.invoice.items = []
          this.selectedTable = new Table()
          // Reset all pending quantities when receipt is saved/completed
          this.itemPendingQuantities = {}
          this.transactionEndedWithReceipt()
        }
      },
      {
        event: 'table-saved-clear-pending',
        handler: () => {
          // Clear pending quantities after table is saved
          this.itemPendingQuantities = {}
        }
      },
      {
        event: 'set-mode',
        handler: (editMode) => {
          this.editMode = editMode
        }
      },
      {
        event: 'set-orders-view',
        handler: (ordersView) => {
          this.ordersView = ordersView
        }
      },
      {
        event: 'keyboard-shortcuts-changed',
        handler: (settings) => {
          this.keyboardShortcutsEnabled = settings.enabled
          this.showShortcutBadges = settings.showBadges
        }
      },
      {
        event: 'modal-close-setting-changed',
        handler: (closeModal) => {
          this.closeModalOnProductClick = closeModal
        }
      },
      {
        event: 'category-display-mode-changed',
        handler: (mode) => {
          this.categoryDisplayMode = mode
        }
      },
      {
        event: 'barcode-scanner-changed',
        handler: (enabled) => {
          this.useBarcodeScanner = enabled
          if (enabled) {
            document.addEventListener('keydown', this.boundBarcodeHandler)
          } else {
            document.removeEventListener('keydown', this.boundBarcodeHandler)
          }
        }
      },
      {
        event: 'adjust-cart',
        handler: (cartConfiguration) => {
          this.cartConfiguration = cartConfiguration
        }
      },
      {
        event: 'quantity-bar-setting-changed',
        handler: (settings) => {
          this.quantityBarEnabled = settings.enabled
          this.quantityBarAutoReset = settings.autoReset
          // Reset quantity if bar is disabled
          if (!settings.enabled) {
            this.selectedQuantity = 1
          }
        }
      }
    ]

    // Register all listeners and store cleanup functions
    listeners.forEach(({ event, handler }) => {
      EventBus.$off(event)
      EventBus.$on(event, handler)
      this.eventBusCleanups.push(() => EventBus.$off(event, handler))
    })

    // Add barcode listener if enabled
    if (this.useBarcodeScanner) {
      document.addEventListener('keydown', this.boundBarcodeHandler)
    }

    // Set up ResizeObserver for product grid height
    this.$nextTick(() => {
      if (this.$refs.productGrid && this.$refs.productGrid.$el) {
        this.productGridResizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const height = entry.contentRect.height
            this.$el.style.setProperty('--product-grid-height', `${height}px`)
          }
        })
        this.productGridResizeObserver.observe(this.$refs.productGrid.$el)
      }
    })
  },

  beforeDestroy () {
    if (this.transactionRedirectTimer) {
      clearTimeout(this.transactionRedirectTimer)
      this.transactionRedirectTimer = null
    }

    // Remove barcode listener
    if (this.useBarcodeScanner && this.boundBarcodeHandler) {
      document.removeEventListener('keydown', this.boundBarcodeHandler)
    }

    // Clear barcode timer
    if (this.barcodeTimer) {
      clearTimeout(this.barcodeTimer)
      this.barcodeTimer = null
    }

    // Disconnect ResizeObserver
    if (this.productGridResizeObserver) {
      this.productGridResizeObserver.disconnect()
      this.productGridResizeObserver = null
    }

    // Execute all EventBus cleanup functions
    this.eventBusCleanups.forEach(cleanup => cleanup())
    this.eventBusCleanups = []

    // Cleanup composables
    if (this.productCacheComposable) {
      this.productCacheComposable.clearProductCache()
    }
    if (this.invoiceCalculationsComposable) {
      this.invoiceCalculationsComposable.cleanup()
    }
  }
}
</script>

<style scoped>
.panel-rows-wide {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
  overflow: hidden;
}

/* Spacing between product grid and invoice/payment panels */
.panels-row {
  margin-top: 8px;
}

/* Add padding to invoice column to prevent overlap with total-row */
.invoice-col {
  padding-bottom: 4rem;
}

.total-row {
  padding: 1rem 1rem 0 0;
  width: 58vw;
  height: 4rem;
  color: white;
  background: linear-gradient(135deg, #307CC1 0%, #36485B 100%);
  box-shadow: 0 2px 8px rgba(48, 124, 193, 0.3);
  position: fixed;
  bottom: 0;
  border-radius: 0 10px 0 0;
  text-align: right;
}
</style>
